type HorizontalChildAlignment = 'start' | 'center'

export function scrollChildHorizontallyInContainer(
  scrollContainer: HTMLElement,
  childElement: HTMLElement,
  options: {
    alignment?: HorizontalChildAlignment
    behavior?: ScrollBehavior
    paddingStartPx?: number
  } = {}
) {
  const alignment = options.alignment ?? 'start'
  const behavior = options.behavior ?? 'smooth'
  const paddingStartPx = options.paddingStartPx ?? 0
  const pinnedVerticalScrollTops = captureVerticalScrollTops(scrollContainer)

  const containerRect = scrollContainer.getBoundingClientRect()
  const childRect = childElement.getBoundingClientRect()
  const deltaLeft = getHorizontalScrollDelta({
    alignment,
    childRect,
    containerRect,
    paddingStartPx
  })

  scrollContainer.scrollTo({
    left: scrollContainer.scrollLeft + deltaLeft,
    behavior
  })

  restorePinnedVerticalScrollTops({
    horizontalScrollElement: scrollContainer,
    behavior,
    pinnedVerticalScrollTops
  })
}

function getHorizontalScrollDelta(input: {
  alignment: HorizontalChildAlignment
  childRect: DOMRect
  containerRect: DOMRect
  paddingStartPx: number
}): number {
  if (input.alignment === 'center') {
    const childCenterX = input.childRect.left + input.childRect.width / 2
    const containerCenterX = input.containerRect.left + input.containerRect.width / 2
    return childCenterX - containerCenterX
  }

  return input.childRect.left - input.containerRect.left - input.paddingStartPx
}

function captureVerticalScrollTops(fromElement: HTMLElement): Map<HTMLElement, number> {
  const pinnedVerticalScrollTops = new Map<HTMLElement, number>()
  let ancestorElement: HTMLElement | null = fromElement.parentElement

  while (ancestorElement) {
    const computedStyle = window.getComputedStyle(ancestorElement)
    const overflowY = computedStyle.overflowY
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      pinnedVerticalScrollTops.set(ancestorElement, ancestorElement.scrollTop)
    }
    ancestorElement = ancestorElement.parentElement
  }

  return pinnedVerticalScrollTops
}

function restorePinnedVerticalScrollTops(input: {
  horizontalScrollElement: HTMLElement
  behavior: ScrollBehavior
  pinnedVerticalScrollTops: Map<HTMLElement, number>
}) {
  const restore = () => {
    input.pinnedVerticalScrollTops.forEach((scrollTop, scrollElement) => {
      scrollElement.scrollTop = scrollTop
    })
  }

  restore()

  if (input.behavior === 'auto') {
    return
  }

  let frameCount = 0
  const maxFrames = 90
  const tick = () => {
    restore()
    frameCount += 1
    if (frameCount < maxFrames) {
      window.requestAnimationFrame(tick)
    }
  }
  window.requestAnimationFrame(tick)
  input.horizontalScrollElement.addEventListener('scrollend', restore, { once: true })
}
